// A single tab entry: a stable id used for selection and a human-readable label.
export interface TabItem<Id extends string = string> {
  id: Id;
  label: string;
}

export interface TabsProps<Id extends string = string> {
  tabs: TabItem<Id>[];
  activeTab: Id;
  onTabChange: (id: Id) => void;
  className?: string;
}

// Tabs renders a presentational tab bar and reports selection changes to the parent.
// It holds no state itself, so the active tab can be driven by local state or the URL.
export function Tabs<Id extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  className = '',
}: TabsProps<Id>) {
  return (
    <div className={`flex gap-x-1 ${className}`} aria-label="Tabs" role="tablist">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            className={`py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg transition-colors ${
              isActive
                ? 'bg-primary text-onprimary'
                : 'bg-transparent text-muted hover:text-onsurface hover:bg-surface-darker'
            } focus:outline-none`}
            onClick={() => onTabChange(tab.id)}
            role="tab"
            aria-selected={isActive}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
